import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import LoadingSpinner from '../common/LoadingSpinner';
import { APP_ROUTES } from '../../constants';
import AccessDenied from './AccessDenied';
import { hasPermission, isEmployerSuperAdmin } from '../../utils/rbac';
import { isEmployerContactVerified, isEmployerRole } from '../../utils/employerOnboarding';

const ProtectedRoute = ({ children, requiresKycVerification = true, permission, superAdminOnly = false }) => {
  const { 
    isAuthenticated, 
    verificationStatus, 
    loading, 
    user,
    refreshVerificationStatus,
    ensureActiveSession,
    isSessionExpired
  } = useAuthStore();
  const location = useLocation();
  const userId = user?.id;
  const employerUser = isEmployerRole(user?.role);
  const contactVerified = isEmployerContactVerified(user);

  const sessionExpired = isAuthenticated && isSessionExpired();

  // Refresh verification status on mount for employer/hr_manager roles
  useEffect(() => {
    const refreshStatus = async () => {
      if (!ensureActiveSession()) return;
      if (location.pathname === APP_ROUTES.KYC_VERIFICATION) return;

      if (isAuthenticated && userId && employerUser && contactVerified) {
        try {
          await refreshVerificationStatus();
        } catch (error) {
          console.warn('ProtectedRoute: Failed to refresh verification status:', error);
        }
      }
    };

    refreshStatus();
  }, [contactVerified, employerUser, ensureActiveSession, isAuthenticated, location.pathname, refreshVerificationStatus, userId]);

  // Show loading spinner while auth is being determined
  if (loading) {
    return React.createElement(LoadingSpinner);
  }

  if (sessionExpired) {
    return React.createElement(Navigate, {
      to: APP_ROUTES.LOGIN,
      state: { reason: 'idle_timeout', from: location },
      replace: true
    });
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return React.createElement(Navigate, {
      to: APP_ROUTES.LOGIN,
      state: { from: location },
      replace: true
    });
  }

  if (
    isEmployerRole(user?.role) &&
    !isEmployerContactVerified(user) &&
    location.pathname !== APP_ROUTES.CONTACT_VERIFICATION
  ) {
    return React.createElement(Navigate, {
      to: APP_ROUTES.CONTACT_VERIFICATION,
      replace: true,
      state: {
        reason: 'contact_verification_required',
        from: location.pathname
      }
    });
  }

  // If KYC verification is required for employers, check verification status
  if (requiresKycVerification && isEmployerRole(user?.role)) {
    // Allow access if verified
    if (verificationStatus === 'verified') {
      return children;
    }

    // Allow access to KYC verification page itself (prevent redirect loops)
    if (location.pathname === APP_ROUTES.KYC_VERIFICATION) {
      return children;
    }

    // Redirect to KYC verification for unverified employers
    return React.createElement(Navigate, {
      to: APP_ROUTES.KYC_VERIFICATION,
      replace: true,
      state: { 
        reason: 'kyc_required',
        currentStatus: verificationStatus,
        from: location.pathname
      }
    });
  }

  if (superAdminOnly && !isEmployerSuperAdmin(user)) {
    return React.createElement(AccessDenied, {
      requiredPermission: permission,
      superAdminOnly: true
    });
  }

  if (permission && !hasPermission(user, permission)) {
    return React.createElement(AccessDenied, {
      requiredPermission: permission
    });
  }

  // Default: allow access
  return children;
};

export default ProtectedRoute;
